




































































































































import { BaseStepForm } from '@/components/forms/onboarding/BaseStepForm';
import { IODSkillsForm } from '@/model';
import { ICategory, IDomain, IProduct  } from '@homeserve/od-typings/api';
import { Nullable } from '@homeserve/od-typings/utils';
import difference from 'lodash/difference';
import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { State } from 'vuex-class';

interface IDomainCategories {
  [key: string]: ICategory[];
}

@Component
export default class SkillsForm extends BaseStepForm<IODSkillsForm> {
  @State('allProducts', { namespace: 'api/catalog/product' })
  public products!: IProduct[];

  @Prop({ type: Boolean, default: false })
  public showProductsChoice!: boolean;

  public domains: IDomain[] = [];
  public domainCategories: IDomainCategories = {};
  public readonly formScope = 'skillsForm';
  public selectedDomain: Nullable<IDomain> = null;
  public selectedCategories: number[] = [];
  public showDialog = false;
  public selectedProducts: string[] = [];

  public async created() {
    await this.attachValidatorFields();
    this.domains = uniqBy(
      this.products.map(product => product.domain),
      domain => domain.slug,
    );
    this.domainCategories = this.products.reduce<IDomainCategories>((accumulator, product) => {
      const { category, domain } = product;
      const categories = accumulator[domain.slug];

      return {
        ...accumulator,
        [domain.slug]: categories ? uniqBy([...categories, category], item => item.id) : [category],
      };
    }, {});
    this.selectedCategories = [
      ...new Set(
        this.products
          .filter(product => this.model.products.indexOf(product.code) !== -1)
          .map(product => product.category.id),
      ),
    ];
    this.selectedProducts = [...this.model.products];
  }

  public get productByCategoriesByDomain() {
    return groupBy(
      this.products.filter(p => p.domain.code === (this.selectedDomain ? this.selectedDomain.code : '')),
      'category.name',
    );
  }

  public hasSelectedAllProducts(categoryName: string) {
    const selectedProducts = this.products.filter(
      p => this.selectedProducts.indexOf(p.code) !== -1 && p.category.name === categoryName,
    ).length;
    const productsBelongingToTheCategory = this.products.filter(p => p.category.name === categoryName).length;

    return selectedProducts === productsBelongingToTheCategory ? 1 : selectedProducts === 0 ? -1 : 0;
  }

  public closeDomainDialog() {
    this.selectedDomain = null;
    this.showDialog = false;
  }

  public countSelectedCategories(domain: IDomain): number {
    return this.domainCategories[domain.slug].filter(category => this.selectedCategories.indexOf(category.id) !== -1)
      .length;
  }

  public selectAllProducts(categoryName: string) {
    const selectedProducts = this.products.filter(p => p.category.name === categoryName).map(p => p.code);
    this.selectedProducts = [...new Set([...this.selectedProducts.concat(selectedProducts)])];
  }

  public unselectAllProducts(categoryName: string) {
    const productsToUnselect = this.products.filter(p => p.category.name === categoryName).map(p => p.code);
    this.selectedProducts = this.selectedProducts.filter(p => productsToUnselect.indexOf(p) === -1);
  }

  public countSelectedProducts(domain: IDomain): number {
    return this.products.filter(p => p.domain.slug === domain.slug && this.selectedProducts.indexOf(p.code) !== -1)
      .length;
  }

  public hasAllSelectedCategories(domain: IDomain): boolean {
    const categories = this.domainCategories[domain.slug].map(category => category.id);

    return categories.every((id: number) => this.selectedCategories.indexOf(id) !== -1);
  }

  public openDomainDialog(domain: IDomain) {
    this.selectedDomain = domain;
    this.showDialog = true;
  }

  public selectAllCategories(domain: IDomain) {
    const categories = this.domainCategories[domain.slug].map(category => category.id);

    this.selectedCategories = [...new Set(this.selectedCategories.concat(categories))];
  }

  public unselectAllCategories(domain: IDomain) {
    const categories = this.domainCategories[domain.slug].map(category => category.id);

    this.selectedCategories = this.selectedCategories.filter((id: number) => categories.indexOf(id) === -1);
  }

  public hasSelectedSomething(domain: IDomain) {
    return (
      (this.showProductsChoice && this.countSelectedProducts(domain) > 0) ||
      (!this.showProductsChoice && this.countSelectedCategories(domain)) > 0
    );
  }

  private async attachValidatorFields() {
    const validate = Object.keys(this.$validator.errors.collect(undefined, this.formScope)).length > 0;

    if (this.$validator.fields.find({ name: 'domains', scope: this.formScope })) {
      this.$validator.detach('domains', this.formScope);
    }

    if (this.$validator.fields.find({ name: 'products', scope: this.formScope })) {
      this.$validator.detach('products', this.formScope);
    }

    this.$validator.attach({
      getter: () => this.model.domains,
      name: 'domains',
      rules: 'required',
      scope: this.formScope,
    });
    this.$validator.attach({
      getter: () => this.model.products,
      name: 'products',
      rules: 'required',
      scope: this.formScope,
    });

    if (validate) {
      await this.isValid();
    }
  }

  @Watch('model.domains')
  private async onDomainsChanged(domains) {
    await this.$validator.validate(`${this.formScope}.domains`, domains);
  }

  @Watch('model.products')
  private async onProductsChanged(products) {
    await this.$validator.validate(`${this.formScope}.products`, products);
  }

  @Watch('selectedProducts')
  private async onSelectedProductsChanged() {
    const products = new Set([...this.selectedProducts]);
    const domains = this.products.filter(p => this.selectedProducts.indexOf(p.code) !== -1).map(p => p.domain.slug);
    this.$emit('change', {
      ...this.model,
      domains: [...new Set(domains)],
      products: [...new Set(products)],
    });
  }

  @Watch('selectedCategories')
  private async onSelectedCategoriesChanged(
    categories: number[],
    previousCategories: number[],
  ): Promise<IODSkillsForm | undefined> {
    const added = difference(categories, previousCategories);
    const removed = difference(previousCategories, categories);

    if (!added.length && !removed.length) {
      return;
    }

    let products = [...this.model.products];

    if (added.length) {
      const productsAdded = this.products
        .filter(product => added.indexOf(product.category.id) !== -1)
        .map(product => product.code);

      products = [...new Set(products.concat(productsAdded))];
    }

    if (removed.length) {
      const productsRemoved = this.products
        .filter(product => removed.indexOf(product.category.id) !== -1)
        .map(product => product.code);

      products = [...new Set(products.filter(productCode => productsRemoved.indexOf(productCode) === -1))];
    }

    const domains = [
      ...new Set(
        this.products.filter(product => products.indexOf(product.code) !== -1).map(product => product.domain.slug),
      ),
    ];

    this.$emit('change', {
      ...this.model,
      domains,
      products,
    });
  }
}
